import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  archiveNetworkAd,
  createNetworkAd,
  createNetworkAdAssignment,
  discoverNetworkAdCampaigns,
  discoverNetworkAdTargets,
  getNetworkAdsRollout,
  getNetworkAdsReport,
  getNetworkAdsWorkspace,
  listNetworkAds,
  pauseNetworkAd,
  removeNetworkAdAssignment,
  updateNetworkAd,
  updateNetworkAdAssignment,
  updateNetworkAdsRollout,
  updateNetworkAdsSiteRollout,
} from './networkAdsApi';
import './NetworkAdsAdmin.css';
import { buildCampaignTree } from './campaignTree';

const errorMessage = (error) => error?.response?.data?.message || error?.message || 'Request failed';

const EMPTY_ASSIGNMENT = {
  targetSiteId: '',
  targetCatalogId: '',
  beforeSectionId: '',
  adminLabel: '',
  suitabilityNote: '',
};
const EMPTY_REPORT = {
  summary: { deliveries: 0, viewableImpressions: 0, campaignOpens: 0, uniqueVisitors: 0, viewabilityRate: 0, ctr: 0 },
  breakdown: [],
};

export function groupTargetSites(targets = []) {
  const sites = new Map();
  for (const target of targets) {
    if (!sites.has(target.targetSiteId)) {
      sites.set(target.targetSiteId, {
        siteId: target.targetSiteId,
        siteName: target.targetSiteName,
      });
    }
  }
  return [...sites.values()].sort((a, b) => a.siteName.localeCompare(b.siteName));
}

function CampaignPreview({ campaign }) {
  if (!campaign) return <div className="network-ads__empty">Choose a campaign to preview it.</div>;
  return (
    <div className="network-campaign-preview">
      {campaign.previewImageUrl ? (
        <img src={campaign.previewImageUrl} alt={`${campaign.title} campaign artwork`} />
      ) : (
        <div className="network-campaign-preview__placeholder">No campaign artwork</div>
      )}
      <div>
        <strong>{campaign.title}</strong>
        <span>{campaign.advertiserSiteName}</span>
        <span>{campaign.ownerOrganizationName || 'Unknown account'}</span>
        <span>{campaign.businessType || campaign.primaryType || 'Category not recorded'}</span>
        {campaign.landingUrl ? (
          <a href={campaign.landingUrl} target="_blank" rel="noreferrer">Preview landing page</a>
        ) : <span className="network-ads__warning">No campaign landing page</span>}
      </div>
    </div>
  );
}

function InventorySettingsEditor({ ad, busy, onSave }) {
  const [weight, setWeight] = useState(String(ad.weight || 1));
  const [attributionWindowHours, setAttributionWindowHours] = useState(String(ad.attributionWindowHours || 168));

  useEffect(() => {
    setWeight(String(ad.weight || 1));
    setAttributionWindowHours(String(ad.attributionWindowHours || 168));
  }, [ad.id, ad.weight, ad.attributionWindowHours]);

  const parsedWeight = Number(weight);
  const valid = Number.isInteger(parsedWeight) && parsedWeight >= 1 && parsedWeight <= 1000;
  const parsedWindow = Number(attributionWindowHours);
  const windowValid = Number.isInteger(parsedWindow) && parsedWindow >= 1 && parsedWindow <= 720;
  const changed = valid && windowValid && (
    parsedWeight !== Number(ad.weight) || parsedWindow !== Number(ad.attributionWindowHours || 168)
  );

  return (
    <div className="network-ads__weight">
      <label>
        Rotation weight
        <input
          type="number"
          min="1"
          max="1000"
          step="1"
          value={weight}
          onChange={(event) => setWeight(event.target.value)}
          disabled={busy || ad.status === 'archived'}
        />
      </label>
      <label>
        Attribution window (hours)
        <input
          type="number"
          min="1"
          max="720"
          step="1"
          value={attributionWindowHours}
          onChange={(event) => setAttributionWindowHours(event.target.value)}
          disabled={busy || ad.status === 'archived'}
        />
      </label>
      <button
        type="button"
        disabled={busy || ad.status === 'archived' || !changed}
        onClick={() => onSave({ weight: parsedWeight, attributionWindowHours: parsedWindow })}
      >
        Save settings
      </button>
    </div>
  );
}

export default function NetworkAdsAdmin() {
  const [workspaceTab, setWorkspaceTab] = useState('overview');
  const [workspace, setWorkspace] = useState({ roles: {}, inventory: [], warnings: [], referrals: {}, traffic: {}, definitions: {} });
  const [ads, setAds] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [targets, setTargets] = useState([]);
  const [rollout, setRollout] = useState({ platformEnabled: false, disabledSites: [] });
  const [selectedCampaignId, setSelectedCampaignId] = useState('');
  const [campaignSearch, setCampaignSearch] = useState('');
  const [expandedOwners, setExpandedOwners] = useState({});
  const [expandedSites, setExpandedSites] = useState({});
  const [category, setCategory] = useState('');
  const [admissionWeight, setAdmissionWeight] = useState('1');
  const [selectedAdId, setSelectedAdId] = useState('');
  const [assignment, setAssignment] = useState(EMPTY_ASSIGNMENT);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [report, setReport] = useState(EMPTY_REPORT);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportFilters, setReportFilters] = useState({
    dimension: 'ad',
    trafficClass: 'live',
    dateFrom: '',
    dateTo: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [inventory, campaignOptions, targetOptions, rolloutState, workspaceState] = await Promise.all([
        listNetworkAds(),
        discoverNetworkAdCampaigns(),
        discoverNetworkAdTargets(),
        getNetworkAdsRollout(),
        getNetworkAdsWorkspace(),
      ]);
      setAds(Array.isArray(inventory) ? inventory : []);
      setCampaigns(Array.isArray(campaignOptions) ? campaignOptions : []);
      setTargets(Array.isArray(targetOptions) ? targetOptions : []);
      setRollout(rolloutState || { platformEnabled: false, disabledSites: [] });
      setWorkspace(workspaceState || { roles: {}, inventory: [], warnings: [], referrals: {}, traffic: {}, definitions: {} });
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadReport = useCallback(async () => {
    setReportLoading(true);
    setError('');
    try {
      const result = await getNetworkAdsReport(reportFilters);
      setReport(result || EMPTY_REPORT);
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setReportLoading(false);
    }
  }, [reportFilters]);

  useEffect(() => { loadReport(); }, [loadReport]);

  const run = async (operation, successMessage) => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      await operation();
      await load();
      if (successMessage) setMessage(successMessage);
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setBusy(false);
    }
  };

  const selectedCampaign = campaigns.find(
    (campaign) => String(campaign.campaignId) === String(selectedCampaignId)
  ) || null;
  const campaignTree = useMemo(
    () => buildCampaignTree(campaigns, campaignSearch),
    [campaigns, campaignSearch]
  );
  const searchActive = Boolean(campaignSearch.trim());
  const selectedAd = ads.find((ad) => String(ad.id) === String(selectedAdId)) || ads[0] || null;
  const targetSites = useMemo(() => groupTargetSites(targets), [targets]);
  const targetCatalogs = targets.filter(
    (target) => String(target.targetSiteId) === String(assignment.targetSiteId)
  );
  const selectedCatalog = targetCatalogs.find(
    (target) => String(target.catalogId) === String(assignment.targetCatalogId)
  ) || null;
  const disabledSiteIds = new Set((rollout.disabledSites || []).map((site) => Number(site.siteId)));

  const chooseCampaign = (campaignId) => {
    const campaign = campaigns.find((entry) => String(entry.campaignId) === String(campaignId));
    setSelectedCampaignId(campaignId);
    setCategory(campaign?.businessType || campaign?.primaryType || '');
  };

  const toggleTreeNode = (setter, key) => {
    setter((current) => ({ ...current, [key]: !current[key] }));
  };

  const admitCampaign = (event) => {
    event.preventDefault();
    if (!selectedCampaign) return;
    run(
      () => createNetworkAd({
        campaignId: selectedCampaign.campaignId,
        advertiserSiteId: selectedCampaign.advertiserSiteId,
        advertiserCategory: category || null,
        weight: Number(admissionWeight),
        status: 'active',
      }),
      'Campaign added to Network Ads inventory.'
    );
  };

  const chooseTargetSite = (siteId) => {
    setAssignment({ ...EMPTY_ASSIGNMENT, targetSiteId: siteId });
  };

  const chooseTargetCatalog = (catalogId) => {
    setAssignment((current) => ({
      ...current,
      targetCatalogId: catalogId,
      beforeSectionId: '',
    }));
  };

  const saveAssignment = (event) => {
    event.preventDefault();
    if (!selectedAd) return;
    run(
      () => createNetworkAdAssignment(selectedAd.id, {
        targetCatalogId: Number(assignment.targetCatalogId),
        beforeSectionId: Number(assignment.beforeSectionId),
        adminLabel: assignment.adminLabel || null,
        suitabilityNote: assignment.suitabilityNote || null,
        enabled: true,
      }),
      'Network Ad assignment saved.'
    );
  };

  const toggleSiteDisabled = (siteId) => {
    const disabled = !disabledSiteIds.has(Number(siteId));
    run(
      () => updateNetworkAdsSiteRollout(siteId, disabled),
      disabled ? 'Network Ads disabled for the selected site.' : 'Site override removed.'
    );
  };

  const downloadReport = async (audience) => {
    const exportReport = await getNetworkAdsReport({
      ...reportFilters,
      dimension: audience === 'publisher' ? 'publisher' : 'advertiser',
    });
    const columns = audience === 'publisher'
      ? ['label', 'deliveries', 'viewableImpressions', 'viewabilityRate', 'campaignOpens']
      : ['label', 'deliveries', 'viewableImpressions', 'campaignOpens', 'linkedVisits', 'vouchersIssued', 'vouchersRedeemed', 'averageMatchConfidence'];
    const rows = [columns, ...(exportReport.breakdown || []).map((row) => columns.map((column) => row[column] ?? ''))];
    const csv = rows.map((row) => row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `network-ads-${audience}-${exportReport.filters?.dateFrom || 'report'}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <section className="network-ads">
      <div className="network-ads__header">
        <div>
          <h3>Network Ads</h3>
          <p>Admit saved campaigns and assign them to explicitly approved menu positions.</p>
        </div>
        <div className="network-ads__header-actions">
          <button type="button" onClick={() => { load(); loadReport(); }} disabled={loading || reportLoading || busy}>Refresh</button>
          <button
            type="button"
            className={rollout.platformEnabled ? 'danger' : 'primary'}
            disabled={busy}
            onClick={() => run(
              () => updateNetworkAdsRollout(!rollout.platformEnabled),
              rollout.platformEnabled
                ? 'Emergency kill switch disabled Network Ads.'
                : 'Network Ads rollout enabled.'
            )}
          >
            {rollout.platformEnabled ? 'Disable network globally' : 'Enable network globally'}
          </button>
        </div>
      </div>

      <div className={`network-ads__rollout ${rollout.platformEnabled ? 'is-enabled' : ''}`}>
        Global serving is <strong>{rollout.platformEnabled ? 'enabled' : 'disabled'}</strong>.
        Inventory and assignments can be prepared while serving is disabled.
      </div>
      {error && <div className="network-ads__error">{error}</div>}
      {message && <div className="network-ads__message">{message}</div>}

      <nav className="network-ads__workspace-tabs" aria-label="Advertising network workspace">
        {[
          ['overview', 'Network overview'],
          ['inventory', 'Inventory'],
          ['advertisers', 'Advertisers'],
          ['performance', 'Performance & referrals'],
          ['quality', 'Quality & safety'],
          ['reports', 'Reports'],
        ].map(([key, label]) => <button type="button" key={key} className={workspaceTab === key ? 'is-active' : ''} onClick={() => setWorkspaceTab(key)}>{label}</button>)}
      </nav>

      {workspaceTab === 'overview' && (
        <div className="network-ads__workspace-stack">
          <div className="network-ads__metric-grid">
            <div><strong>{workspace.roles?.inventorySites || 0}</strong><span>Free/Basic supply sites</span></div>
            <div><strong>{workspace.roles?.advertiserSites || 0}</strong><span>Standard+ advertisers</span></div>
            <div><strong>{ads.filter((ad) => ad.status === 'active').length}</strong><span>Active ads</span></div>
            <div><strong>{ads.reduce((sum, ad) => sum + ad.assignments.filter((item) => item.enabled).length, 0)}</strong><span>Active assignments</span></div>
            <div><strong>{report.summary?.deliveries || 0}</strong><span>Deliveries</span></div>
            <div><strong>{report.summary?.linkedVisits || 0}</strong><span>Linked visits</span></div>
            <div><strong>Unavailable</strong><span>Fill opportunities not yet instrumented</span></div>
          </div>
          <div className="network-ads__workspace-grid">
            <div className="network-ads__panel"><h4>Commercial roles</h4><p>{workspace.definitions?.supply}</p><p>{workspace.definitions?.advertisers}</p>{(workspace.inventory || []).map((row) => <div className="network-ads__workspace-row" key={row.packageKey}><span>{row.packageKey}</span><strong>{row.menus} menus · {row.slots} slots</strong></div>)}</div>
            <div className="network-ads__panel"><h4>Needs attention</h4>{workspace.warnings?.slice(0, 5).map((warning, index) => <div className={`network-ads__warning-card is-${warning.severity}`} key={`${warning.type}-${index}`}><strong>{warning.type}</strong><span>{warning.message}</span></div>)}{!workspace.warnings?.length && <div className="network-ads__empty">No current network warnings.</div>}</div>
          </div>
        </div>
      )}

      {(workspaceTab === 'overview' || workspaceTab === 'performance') && (
      <div className="network-ads__panel network-ads__report">
        <div className="network-ads__report-header">
          <div>
            <h4>Performance</h4>
            <span>CTR is campaign opens divided by viewable impressions.</span>
          </div>
          <div className="network-ads__report-filters">
            <label>
              Group by
              <select value={reportFilters.dimension} onChange={(event) => setReportFilters((current) => ({ ...current, dimension: event.target.value }))}>
                <option value="ad">Network Ad</option>
                <option value="advertiser">Advertiser</option>
                <option value="publisher">Publisher site</option>
                <option value="menu">Menu</option>
                <option value="slot">Slot</option>
                <option value="date">Date</option>
              </select>
            </label>
            <label>
              Traffic
              <select value={reportFilters.trafficClass} onChange={(event) => setReportFilters((current) => ({ ...current, trafficClass: event.target.value }))}>
                <option value="live">Live</option>
                <option value="preview">Preview</option>
                <option value="test">Test</option>
                <option value="all">All</option>
              </select>
            </label>
            <label>
              From
              <input type="date" value={reportFilters.dateFrom} onChange={(event) => setReportFilters((current) => ({ ...current, dateFrom: event.target.value }))} />
            </label>
            <label>
              To
              <input type="date" value={reportFilters.dateTo} onChange={(event) => setReportFilters((current) => ({ ...current, dateTo: event.target.value }))} />
            </label>
          </div>
        </div>
        <div className="network-ads__metric-grid">
          <div><strong>{report.summary?.deliveries || 0}</strong><span>Deliveries</span></div>
          <div><strong>{report.summary?.viewableImpressions || 0}</strong><span>Viewable</span></div>
          <div><strong>{report.summary?.campaignOpens || 0}</strong><span>Opens</span></div>
          <div><strong>{((report.summary?.ctr || 0) * 100).toFixed(1)}%</strong><span>CTR</span></div>
          <div><strong>{report.summary?.uniqueVisitors || 0}</strong><span>Unique visitors</span></div>
          <div><strong>{report.summary?.linkedVisits || 0}</strong><span>Linked visits</span></div>
          <div><strong>{report.summary?.vouchersIssued || 0}</strong><span>Vouchers issued</span></div>
          <div><strong>{report.summary?.vouchersRedeemed || 0}</strong><span>Vouchers redeemed</span></div>
        </div>
        <div className="network-ads__report-note">
          <strong>{report.attribution?.commercialStatus || 'Non-billable POC evidence'}</strong>
          <span>{report.attribution?.creditRuleLabel || 'Last eligible click; otherwise last viewable impression'}</span>
          <span>{report.attribution?.qualification || 'Linked visits are attribution evidence, not proof of causation.'}</span>
        </div>
        {reportLoading ? (
          <div className="network-ads__empty">Loading report…</div>
        ) : (
          <div className="network-ads__report-table-wrap">
            <table className="network-ads__report-table">
              <thead><tr><th>Group</th><th>Delivered</th><th>Viewable</th><th>Viewability</th><th>Opens</th><th>CTR</th><th>Visitors</th><th>Linked visits</th><th>Vouchers issued / redeemed</th><th>Click / view credit</th><th>Confidence</th></tr></thead>
              <tbody>
                {(report.breakdown || []).map((row) => (
                  <tr key={`${reportFilters.dimension}:${row.id || row.label}`}>
                    <td>{row.label || 'Unknown'}</td>
                    <td>{row.deliveries}</td>
                    <td>{row.viewableImpressions}</td>
                    <td>{((row.viewabilityRate || 0) * 100).toFixed(1)}%</td>
                    <td>{row.campaignOpens}</td>
                    <td>{((row.ctr || 0) * 100).toFixed(1)}%</td>
                    <td>{row.uniqueVisitors}</td>
                    <td title={`${row.wifiConnectionVisits || 0} Wi-Fi connections; ${row.venuePageVisits || 0} venue page observations`}>{row.linkedVisits || 0}</td>
                    <td>{`${row.vouchersIssued || 0} / ${row.vouchersRedeemed || 0}`}</td>
                    <td>{`${row.clickThroughVisits || 0} / ${row.viewThroughVisits || 0}`}</td>
                    <td>{row.averageMatchConfidence === null ? '—' : `${Number(row.averageMatchConfidence).toFixed(0)}%`}</td>
                  </tr>
                ))}
                {!report.breakdown?.length && <tr><td colSpan="11">No delivery data for this period.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
      )}

      {workspaceTab === 'advertisers' && <div className="network-ads__panel"><h4>Advertisers</h4><p>Standard-and-above venues with admitted campaigns. Campaign ownership remains grouped in the inventory admission tree.</p><div className="network-ads__workspace-grid">{ads.map((ad) => <article className="network-ads__advertiser-card" key={ad.id}><strong>{ad.advertiserSiteName}</strong><span>{ad.campaignTitle}</span><span>{ad.status} · {ad.assignments.length} assignments</span><span>{ad.attributionWindowHours} hour attribution window</span></article>)}</div></div>}

      {workspaceTab === 'performance' && <div className="network-ads__panel"><h4>Recommendations and referrals</h4><p>{workspace.definitions?.referrals}</p><div className="network-ads__metric-grid"><div><strong>{workspace.referrals?.destination_open || 0}</strong><span>Destination opens</span></div><div><strong>{workspace.referrals?.activity_hub_open || 0}</strong><span>Activity Hub opens</span></div><div><strong>{workspace.referrals?.inline_recommendation_open || 0}</strong><span>Inline recommendation opens</span></div></div></div>}

      {workspaceTab === 'quality' && <div className="network-ads__workspace-grid"><div className="network-ads__panel"><h4>Quality and safety</h4>{report.summary?.deliveries >= 20 && report.summary?.viewabilityRate < 0.5 && <div className="network-ads__warning-card is-medium"><strong>viewability</strong><span>Fewer than half of recent deliveries became viewable; review placement position and menu depth.</span></div>}<div className="network-ads__warning-card is-info"><strong>fill coverage</strong><span>Eligible ad opportunities are not yet recorded, so true fill rate is unavailable rather than reported as zero.</span></div>{workspace.warnings?.map((warning, index) => <div className={`network-ads__warning-card is-${warning.severity}`} key={`${warning.type}-${index}`}><strong>{warning.type}</strong><span>{warning.message}</span></div>)}{!workspace.warnings?.length && <div className="network-ads__empty">No additional current warnings.</div>}</div><div className="network-ads__panel"><h4>Safety controls</h4><p>The global kill switch remains at the top of this workspace. Site overrides remain beside assignment controls to reduce accidental changes.</p><div className="network-ads__workspace-row"><span>Global serving</span><strong>{rollout.platformEnabled ? 'Enabled' : 'Disabled'}</strong></div><div className="network-ads__workspace-row"><span>Disabled sites</span><strong>{rollout.disabledSites?.length || 0}</strong></div><div className="network-ads__workspace-row"><span>Recent live / preview / test</span><strong>{workspace.traffic?.live || 0} / {workspace.traffic?.preview || 0} / {workspace.traffic?.test || 0}</strong></div></div></div>}

      {workspaceTab === 'reports' && <div className="network-ads__workspace-grid"><div className="network-ads__panel"><h4>Advertiser report</h4><p>Campaign delivery, opens, linked visits, voucher outcomes, confidence, and attribution qualifications.</p><button type="button" onClick={() => downloadReport('advertiser')}>Download advertiser CSV</button></div><div className="network-ads__panel"><h4>Publisher report</h4><p>Supply delivery, viewability, and campaign opens for inventory partners.</p><button type="button" onClick={() => downloadReport('publisher')}>Download publisher CSV</button></div><div className="network-ads__panel"><h4>Report qualifications</h4><p>{report.attribution?.qualification}</p><p>{report.attribution?.creditRuleLabel}</p><strong>{report.attribution?.commercialStatus}</strong></div></div>}

      {workspaceTab === 'inventory' && (
      <div className="network-ads__grid">
        <form className="network-ads__panel" onSubmit={admitCampaign}>
          <h4>1. Select advertiser campaign</h4>
          <label>
            Find owner, site, or campaign
            <input
              type="search"
              value={campaignSearch}
              onChange={(event) => setCampaignSearch(event.target.value)}
              placeholder="Search campaigns…"
            />
          </label>
          <div className="network-campaign-tree" role="tree" aria-label="Saved campaigns by owner and site">
            {!campaignTree.length && (
              <div className="network-ads__empty">No campaigns match this search.</div>
            )}
            {campaignTree.map((owner) => {
              const ownerOpen = searchActive || Boolean(expandedOwners[owner.ownerId]);
              const ownerCampaignCount = owner.sites.reduce((total, site) => total + site.campaigns.length, 0);
              return (
                <div key={owner.ownerId} className="network-campaign-tree__owner" role="treeitem" aria-expanded={ownerOpen}>
                  <button type="button" className="network-campaign-tree__branch" onClick={() => toggleTreeNode(setExpandedOwners, owner.ownerId)}>
                    <span aria-hidden="true">{ownerOpen ? '▾' : '▸'}</span>
                    <strong>{owner.ownerName}</strong>
                    <span>{ownerCampaignCount}</span>
                  </button>
                  {ownerOpen && (
                    <div className="network-campaign-tree__sites" role="group">
                      {owner.sites.map((site) => {
                        const siteKey = `${owner.ownerId}:${site.siteId}`;
                        const siteOpen = searchActive || Boolean(expandedSites[siteKey]);
                        return (
                          <div key={siteKey} role="treeitem" aria-expanded={siteOpen}>
                            <button type="button" className="network-campaign-tree__branch network-campaign-tree__branch--site" onClick={() => toggleTreeNode(setExpandedSites, siteKey)}>
                              <span aria-hidden="true">{siteOpen ? '▾' : '▸'}</span>
                              <strong>{site.siteName}</strong>
                              <span>{site.campaigns.length}</span>
                            </button>
                            {siteOpen && (
                              <div className="network-campaign-tree__campaigns" role="group">
                                {site.campaigns.map((campaign) => {
                                  const admitted = Boolean(campaign.networkAdId);
                                  const selected = String(campaign.campaignId) === String(selectedCampaignId);
                                  return (
                                    <button
                                      type="button"
                                      role="treeitem"
                                      key={campaign.campaignId}
                                      className={`network-campaign-tree__campaign${selected ? ' is-selected' : ''}`}
                                      onClick={() => chooseCampaign(campaign.campaignId)}
                                      disabled={busy}
                                    >
                                      <span>{campaign.title || `Campaign ${campaign.campaignId}`}</span>
                                      {admitted && <small>Already admitted</small>}
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <CampaignPreview campaign={selectedCampaign} />
          <label>
            Advertiser category
            <input value={category} onChange={(event) => setCategory(event.target.value)} maxLength={128} />
          </label>
          <label>
            Rotation weight
            <input
              type="number"
              min="1"
              max="1000"
              step="1"
              value={admissionWeight}
              onChange={(event) => setAdmissionWeight(event.target.value)}
            />
            <span className="network-ads__field-help">Relative chance when several eligible ads share a menu position.</span>
          </label>
          <button
            className="primary"
            type="submit"
            disabled={
              busy
              || !selectedCampaign
              || selectedCampaign.networkAdId
              || !Number.isInteger(Number(admissionWeight))
              || Number(admissionWeight) < 1
              || Number(admissionWeight) > 1000
            }
          >
            Add active Network Ad
          </button>
        </form>

        <div className="network-ads__panel">
          <h4>2. Inventory</h4>
          {loading && <div className="network-ads__empty">Loading inventory…</div>}
          {!loading && !ads.length && <div className="network-ads__empty">No Network Ads admitted yet.</div>}
          <div className="network-ads__inventory">
            {ads.map((ad) => (
              <article key={ad.id} className={selectedAd?.id === ad.id ? 'is-selected' : ''}>
                <button type="button" className="network-ads__select" onClick={() => setSelectedAdId(ad.id)}>
                  <strong>{ad.campaignTitle}</strong>
                  <span>{ad.advertiserSiteName}</span>
                  <span className={`network-ads__status status-${ad.status}`}>{ad.status}</span>
                </button>
                <div className="network-ads__row-actions">
                  {ad.landingKey && <a href={`/lp/c/${encodeURIComponent(ad.landingKey)}`} target="_blank" rel="noreferrer">Preview</a>}
                  {ad.status !== 'active' && ad.status !== 'archived' && (
                    <button type="button" disabled={busy} onClick={() => run(() => updateNetworkAd(ad.id, { status: 'active' }), 'Network Ad activated.')}>Activate</button>
                  )}
                  {ad.status === 'active' && (
                    <button type="button" disabled={busy} onClick={() => run(() => pauseNetworkAd(ad.id), 'Network Ad paused.')}>Pause</button>
                  )}
                  {ad.status !== 'archived' && (
                    <button type="button" className="danger" disabled={busy} onClick={() => {
                      if (window.confirm(`Archive “${ad.campaignTitle}”?`)) {
                        run(() => archiveNetworkAd(ad.id), 'Network Ad archived.');
                      }
                    }}>Archive</button>
                  )}
                </div>
                <InventorySettingsEditor
                  ad={ad}
                  busy={busy}
                  onSave={(settings) => run(
                    () => updateNetworkAd(ad.id, settings),
                    'Network Ad settings updated.'
                  )}
                />
              </article>
            ))}
          </div>
        </div>

        <form className="network-ads__panel network-ads__assignment" onSubmit={saveAssignment}>
          <h4>3. Assign {selectedAd ? `“${selectedAd.campaignTitle}”` : 'an ad'}</h4>
          <div className="network-ads__assignment-fields">
            <label>
              Target site
              <select value={assignment.targetSiteId} onChange={(event) => chooseTargetSite(event.target.value)} required disabled={!selectedAd || busy}>
                <option value="">Choose a site…</option>
                {targetSites.map((site) => <option key={site.siteId} value={site.siteId}>{site.siteName}</option>)}
              </select>
            </label>
            <label>
              Menu
              <select value={assignment.targetCatalogId} onChange={(event) => chooseTargetCatalog(event.target.value)} required disabled={!assignment.targetSiteId || busy}>
                <option value="">Choose a menu…</option>
                {targetCatalogs.map((target) => <option key={target.catalogId} value={target.catalogId}>{target.catalogTitle || `Menu ${target.catalogId}`}</option>)}
              </select>
            </label>
            <label>
              Insert before section
              <select value={assignment.beforeSectionId} onChange={(event) => setAssignment((current) => ({ ...current, beforeSectionId: event.target.value }))} required disabled={!selectedCatalog || busy}>
                <option value="">Choose a section…</option>
                {(selectedCatalog?.sections || []).map((section) => (
                  <option key={section.sectionId} value={section.sectionId} disabled={!section.enabled}>
                    {section.sectionName}{section.enabled ? '' : ' (disabled)'}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Administrator label
              <input value={assignment.adminLabel} onChange={(event) => setAssignment((current) => ({ ...current, adminLabel: event.target.value }))} maxLength={255} />
            </label>
          </div>
          <label>
            Suitability/category review note
            <textarea value={assignment.suitabilityNote} onChange={(event) => setAssignment((current) => ({ ...current, suitabilityNote: event.target.value }))} maxLength={5000} placeholder="Why is this advertiser appropriate for this menu?" />
          </label>
          <div className="network-ads__form-actions">
            <button className="primary" type="submit" disabled={!selectedAd || !assignment.beforeSectionId || busy}>Save assignment</button>
            {selectedCatalog?.landingKey && <a href={`/lp/m/${encodeURIComponent(selectedCatalog.landingKey)}?network_ad_mode=preview`} target="_blank" rel="noreferrer">Preview target menu</a>}
            {assignment.targetSiteId && (
              <button type="button" className={disabledSiteIds.has(Number(assignment.targetSiteId)) ? 'primary' : 'danger'} disabled={busy} onClick={() => toggleSiteDisabled(assignment.targetSiteId)}>
                {disabledSiteIds.has(Number(assignment.targetSiteId)) ? 'Allow ads for site' : 'Disable ads for site'}
              </button>
            )}
          </div>
        </form>

        <div className="network-ads__panel network-ads__assignments">
          <h4>Current assignments</h4>
          {!selectedAd?.assignments?.length && <div className="network-ads__empty">No assignments for this Network Ad.</div>}
          {(selectedAd?.assignments || []).map((item) => (
            <article key={item.id}>
              <div>
                <strong>{item.targetSiteName}</strong>
                <span>{item.targetCatalogTitle || `Menu ${item.targetCatalogId}`} · before {item.beforeSectionName}</span>
                <span>{item.suitabilityNote || 'No suitability note recorded.'}</span>
              </div>
              <div className="network-ads__row-actions">
                {item.targetCatalogLandingKey && <a href={`/lp/m/${encodeURIComponent(item.targetCatalogLandingKey)}?network_ad_mode=preview`} target="_blank" rel="noreferrer">Preview menu</a>}
                <button type="button" disabled={busy} onClick={() => run(
                  () => updateNetworkAdAssignment(item.id, { enabled: !item.enabled }),
                  item.enabled ? 'Assignment disabled.' : 'Assignment enabled.'
                )}>{item.enabled ? 'Disable' : 'Enable'}</button>
                <button type="button" className="danger" disabled={busy} onClick={() => {
                  if (window.confirm('Remove this Network Ad assignment?')) {
                    run(() => removeNetworkAdAssignment(item.id), 'Assignment removed.');
                  }
                }}>Remove</button>
              </div>
            </article>
          ))}
        </div>
      </div>
      )}
    </section>
  );
}
